// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import { useContext, useState } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Logout from "@mui/icons-material/Logout";
import { AuthContext } from "../../context/AuthContext";
import { RootState, useAppSelector } from "../../slices/store";
import LoginIcon from "@mui/icons-material/Login";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

export default function MenuPopper() {
  const authContext = useContext(AuthContext);

  const auth = useAppSelector((state: RootState) => state.auth);
  const contactState = useAppSelector((state: RootState) => state.contact);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <React.Fragment>
      {auth.isAuthenticated ? (
        <>
        <Box sx={{ display: "flex", alignItems: "center", textAlign: "center" }}>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 2 }}
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: "secondary.main" }} />
        </IconButton>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
          <Box minWidth={250}>
            <MenuItem
              sx={{
                pointerEvents: "none",
                fontWeight: "600",
                paddingBottom: 2,
              }}
            >
              <Avatar />{" "}
              {`${contactState.response.firstName} ${contactState.response.lastName}`}
            </MenuItem>

            <MenuItem sx={{ pointerEvents: "none" }}>
              <ListItemIcon>
                <BadgeOutlinedIcon />
              </ListItemIcon>
              {contactState.response.certificationId}
            </MenuItem>
            <MenuItem sx={{ pointerEvents: "none" }}>
              <ListItemIcon>
                <EmailOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {auth.userInfo?.email}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                authContext.appSignOut();
              }}
            >
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Box>
      </Menu>
        </>
      ) : (
        <Box>
            <MenuItem
              onClick={() => {
                authContext.appSignIn();
              }}
            >
              <ListItemIcon>
                <LoginIcon fontSize="small" />
              </ListItemIcon>
              Login
            </MenuItem>
          </Box>
        )}
    </React.Fragment>
  );
}
